import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import './ManutenzioneAutomezzi.css';
import RotateDevice from '../components/RotateDevice/RotateDevice';
import Select from "react-select";
import FiltroCategorie from '../components/FiltroCategorie/FiltroCategorie';
import { MOCKED_MANUTENZIONE_CATEGORIES } from '../mocks/MockedCategories';
import Lf23Loader from "../components/Lf23Loader";
import ManutenzioneTagliandi, { InfoTagliandi } from "./ManutenzioneTagliandi";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Etichette prodotte dal backend quando la posizione serale non da un cantiere reale.
// Devono restare allineate a manutenzioneController.js.
const CANTIERE_ALTRO = "Altro";
const CANTIERE_NON_RILEVATO = "Non rilevato";
const CANTIERI_SPECIALI = [CANTIERE_ALTRO, CANTIERE_NON_RILEVATO];

const TAB_ANAGRAFICA = MOCKED_MANUTENZIONE_CATEGORIES[0];  // "Anagrafica"
const TAB_POSIZIONE = MOCKED_MANUTENZIONE_CATEGORIES[1];   // "Posizione serale"
// Terza tab: altri dati (situazione tagliandi a km), quindi un componente a se'.
const TAB_TAGLIANDI = MOCKED_MANUTENZIONE_CATEGORIES[2];   // "Tagliandi"

// L'unica differenza fra le due tab: quale campo della riga si legge come cantiere.
const CANTIERE_FIELD_BY_TAB = {
    [TAB_ANAGRAFICA]: "cantiere",
    [TAB_POSIZIONE]: "cantiere_posizione",
};

// Un mezzo e "spostato" se la posizione serale lo mette in un cantiere reale diverso da
// quello in anagrafica. "Altro" e "Non rilevato" sono assenza di dato, non uno spostamento.
const isSpostato = (item) =>
    !!item.cantiere_posizione &&
    !CANTIERI_SPECIALI.includes(item.cantiere_posizione) &&
    item.cantiere_posizione !== item.cantiere;

const formatDataOra = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};

// Il cantiere di anagrafica NON sta piu qui: e scritto in chiaro nella cella, perche
// un'informazione disponibile solo in hover e irraggiungibile da touch e non si scopre.
// Nel tooltip resta la sola data del fix GPS, che e informazione secondaria.
const tooltipRilevazione = (item) => {
    const quando = formatDataOra(item.posizione_updated_at);
    return quando
        ? `Posizione rilevata il ${quando}`
        : 'Data della rilevazione non disponibile';
};

const ManutenzioneAutomezzi = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState("table");
    const [searchTerm, setSearchTerm] = useState("");

    // Tab attiva: cambia SOLO la sorgente del cantiere, il resto della pagina e identico
    const [selectedTab, setSelectedTab] = useState(TAB_ANAGRAFICA);

    // Filtri
    const [soloSpostati, setSoloSpostati] = useState(false);
    const [selectedTipologia, setSelectedTipologia] = useState([]);
    // Bozza dei campi del pannello filtri: passa a searchTerm/selectedTipologia solo con
    // "Applica" (o Invio sulla targa), come nella tab Tagliandi. Chip e toggle restano immediati.
    const [bozzaSearch, setBozzaSearch] = useState("");
    const [bozzaTipologia, setBozzaTipologia] = useState([]);
    const [selectedCantieri, setSelectedCantieri] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState({});

    // Sorting
    const [sortKey, setSortKey] = useState("targa");
    const [sortDir, setSortDir] = useState("asc");
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen]);

    // Stats
    const [stats, setStats] = useState({
        totaleVeicoli: 0,
        manutenzioneCompleta: 0,
        manutenzioneParziale: 0,
        senzaManutenzione: 0,
        inCantiereDiverso: 0
    });
    const [bimestreStats, setBimestreStats] = useState([]);

    // Tooltip custom del badge di discordanza. Due motivi per non usare title=":
    //  - il tooltip nativo compare dopo ~1s e il ritardo non e configurabile;
    //  - un tooltip in CSS verrebbe tagliato da .table-container-compact { overflow: hidden }.
    // Quindi: un solo elemento in position: fixed, posizionato sulle coordinate del badge.
    const [tooltip, setTooltip] = useState(null);

    const showTooltip = (e, text) => {
        const r = e.currentTarget.getBoundingClientRect();
        const sotto = r.top < 60; // troppo vicino al bordo alto: si ribalta sotto il badge
        setTooltip({
            text,
            // clamp orizzontale per non uscire dallo schermo sulle colonne ai bordi
            x: Math.min(Math.max(r.left + r.width / 2, 140), window.innerWidth - 140),
            y: sotto ? r.bottom : r.top,
            sotto
        });
    };

    const hideTooltip = () => setTooltip(null);

    // Indirezione centrale: da qui in giu la pagina non sa piu da dove viene il cantiere.
    const cantiereField = CANTIERE_FIELD_BY_TAB[selectedTab] || "cantiere";
    const getCantiere = (item) => item[cantiereField];
    const isTabPosizione = selectedTab === TAB_POSIZIONE;

    // Fix GPS piu recente nel dataset. NON e l'ora in cui ha girato la DAG: quella non e in
    // tabella. I mezzi smettono di trasmettere da spenti, quindi alle 19:30 il segnale piu
    // fresco e gia quello di fine turno. La DATA resta comunque il segnale che serve: se
    // resta indietro di giorni, vuol dire che la DAG e ferma.
    const ultimaRilevazione = (() => {
        let max = null;
        for (const item of data) {
            if (!item.posizione_updated_at) continue;
            const t = new Date(item.posizione_updated_at).getTime();
            if (!Number.isNaN(t) && (max === null || t > max)) max = t;
        }
        if (max === null) return null;
        const d = new Date(max);
        return {
            data: d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            ora: d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
        };
    })();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = () => {
        setLoading(true);
        axios.get(`${API_URL}/interacta/manutenzione-automezzi`)
            .then(response => {
                setLabels(response.data.bimestriLabels);
                setData(response.data.data);

                // Inizializza colonne visibili
                const initialVisibleColumns = {};
                response.data.bimestriLabels.forEach((_, index) => {
                    initialVisibleColumns[index] = true;
                });
                setVisibleColumns(initialVisibleColumns);

                // La selezione dei cantieri e gestita dall'useEffect su [data, selectedTab],
                // perche la lista dipende dalla tab attiva.
                setLoading(false);
            })
            .catch(error => {
                console.error('Errore nel caricamento dati manutenzione:', error);
                setLoading(false);
            });
    };

    // Calcola statistiche
    useEffect(() => {
        if (data.length === 0) return;

        const totale = filteredData.length;
        let completa = 0;
        let parziale = 0;
        let senza = 0;

        filteredData.forEach(veicolo => {
            const bimestriCompilati = veicolo.bimestri.filter(b => b.value && b.value !== '-').length;
            const totaleBimestri = veicolo.bimestri.length;

            if (bimestriCompilati === totaleBimestri) {
                completa++;
            } else if (bimestriCompilati > 0) {
                parziale++;
            } else {
                senza++;
            }
        });

        setStats({
            totaleVeicoli: totale,
            manutenzioneCompleta: completa,
            manutenzioneParziale: parziale,
            senzaManutenzione: senza,
            inCantiereDiverso: filteredData.filter(isSpostato).length
        });

        // Calcola statistiche per bimestre
        const bimStats = labels.map((_, i) => {
            const total = filteredData.length;
            const filled = filteredData.filter(v => {
                const val = v.bimestri[i]?.value;
                return val && val !== '-' && val !== null;
            }).length;
            const percentage = total > 0 ? Math.round((filled / total) * 100) : 0;
            return { percentage, filled, total };
        });
        setBimestreStats(bimStats);
    }, [data, labels, selectedTipologia, selectedCantieri, searchTerm, selectedTab, soloSpostati]);

    // Filtra dati
    const filteredData = data.filter(item => {
        const matchSearch = searchTerm === "" ||
            item.targa.toLowerCase().includes(searchTerm.toLowerCase());

        const matchTipologia = selectedTipologia.length === 0 ||
            selectedTipologia.includes(item.tipologia);

        const cantiere = getCantiere(item);
        const matchCantiere = selectedCantieri.length === 0 ||
            (cantiere && selectedCantieri.includes(cantiere));

        // Attivo solo sulla tab posizione: sull'anagrafica il concetto non esiste.
        const matchSpostati = !isTabPosizione || !soloSpostati || isSpostato(item);

        return matchSearch && matchTipologia && matchCantiere && matchSpostati;
    }).sort((a, b) => {
        // sortKey resta semantico ('cantiere'): il campo reale dipende dalla tab attiva
        const sortField = sortKey === 'cantiere' ? cantiereField : sortKey;
        const aValue = a[sortField]?.toLowerCase() || '';
        const bValue = b[sortField]?.toLowerCase() || '';
        if (aValue < bValue) return sortDir === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const tipologieUniche = [...new Set(data.map(item => item.tipologia))];
    // Cantieri della tab attiva, alfabetici con "Altro"/"Non rilevato" spinti in fondo:
    // non sono sedi vere e in cima sembrerebbero cantieri reali.
    const cantieriUnici = (() => {
        const tutti = [...new Set(data.map(item => getCantiere(item)).filter(Boolean))];
        const reali = tutti
            .filter(c => !CANTIERI_SPECIALI.includes(c))
            .sort((a, b) => a.localeCompare(b));
        const speciali = CANTIERI_SPECIALI.filter(c => tutti.includes(c));
        return [...reali, ...speciali];
    })();

    // La lista cantieri cambia con la tab: i chip scelti sull'anagrafica non matcherebbero
    // piu nulla sulla posizione serale e la tabella resterebbe vuota. Si riselezionano tutti.
    useEffect(() => {
        setSelectedCantieri(cantieriUnici);
        setSoloSpostati(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, selectedTab]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const getMaintenanceStatus = (value) => {
        if (!value || value === '-' || value === null) {
            return {
                color: '#FEF2F2',
                borderColor: '#FECACA',
                textColor: '#DC2626',
                icon: '❌',
                text: 'Non effettuato'
            };
        }
        return {
            color: '#F0FDF4',
            borderColor: '#BBF7D0',
            textColor: '#059669',
            icon: '✓',
            text: value
        };
    };

    const getVehicleIcon = (tipologia) => {
        return tipologia?.toLowerCase().includes('trasporto persone') || tipologia?.toLowerCase().includes('van')
            ? '🚐'
            : '🚚';
    };

    const handleApplyFilters = () => {
        setSearchTerm(bozzaSearch);
        setSelectedTipologia(bozzaTipologia);
    };

    const handleResetFilters = () => {
        setBozzaSearch("");
        setBozzaTipologia([]);
        setSearchTerm("");
        setSelectedTipologia([]);
        setSoloSpostati(false);
        setSelectedCantieri(cantieriUnici);
    };

    return (
        <div className="manutenzione-automezzi-modern">
            {/* Header */}
            <div className="page-header">
                <div className="header-content">
                    <button
                        className="back-button"
                        onClick={() => navigate("/produttivita")}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Indietro
                    </button>

                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Manutenzione Automezzi</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(true)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">
                            Monitoraggio dello stato della manutenzione periodica dei veicoli aziendali
                        </p>
                    </div>
                </div>
            </div>

            {/* Info Modal */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni Manutenzione Automezzi</h3>
                            <button
                                className="modal-close"
                                onClick={() => setShowInfo(false)}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            {/* La tab Tagliandi ha dati e logica propri: la sua spiegazione e' a parte */}
                            {selectedTab === TAB_TAGLIANDI ? <InfoTagliandi /> : (<>
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    In questa sezione è possibile monitorare lo stato della <strong>manutenzione periodica</strong> dei veicoli aziendali.
                                    Per ogni mezzo viene visualizzata la <strong>targa</strong>, la <strong>tipologia</strong> e lo storico degli interventi
                                    suddiviso per <strong>bimestri</strong>, aggiornati dinamicamente fino al periodo corrente.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Indicatori (KPI)</h4>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li>
                                        <strong>Totale Veicoli:</strong> Numero totale di veicoli presenti nel sistema e visualizzati in base ai filtri applicati
                                    </li>
                                    <li>
                                        <strong>Manutenzione Completa:</strong> Veicoli che hanno completato tutti gli interventi di manutenzione previsti per tutti i bimestri
                                    </li>
                                    <li>
                                        <strong>Manutenzione Parziale:</strong> Veicoli che hanno effettuato alcuni interventi ma non tutti i bimestri sono stati coperti
                                    </li>
                                    <li>
                                        <strong>Senza Manutenzione:</strong> Veicoli che non hanno alcun intervento di manutenzione registrato nel periodo considerato
                                    </li>
                                </ul>
                            </div>

                            <div className="modal-section">
                                <h4>Informazioni visualizzate</h4>
                                <p>Per ogni veicolo vengono mostrati:</p>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li>La <strong>targa</strong> e la <strong>tipologia</strong> del veicolo</li>
                                    <li>Lo stato degli interventi per ogni <strong>bimestre</strong> (Check, Tagliando o entrambi)</li>
                                    <li>Una <strong>colorazione intuitiva</strong> per identificare rapidamente lo stato:
                                        <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                                            <li>Verde: Manutenzione effettuata</li>
                                            <li>Rosso: Manutenzione non effettuata</li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>

                            <div className="modal-section">
                                <h4>Barre di avanzamento</h4>
                                <p>
                                    Nella parte superiore di ogni colonna bimestre è presente una <strong>barra di avanzamento</strong> che indica:
                                </p>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li>La <strong>percentuale di veicoli</strong> per cui la manutenzione risulta completata nel periodo corrispondente</li>
                                    <li>Il <strong>conteggio numerico</strong> (es. "6/8" indica 6 veicoli su 8 totali hanno completato la manutenzione)</li>
                                </ul>
                            </div>

                            <div className="modal-section">
                                <h4>Le due tab: Anagrafica e Posizione serale</h4>
                                <p>
                                    La pagina mostra gli stessi veicoli in due modi, che differiscono <strong>solo</strong> per
                                    come il mezzo viene associato a un cantiere:
                                </p>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li>
                                        <strong>Anagrafica:</strong> il cantiere è quello registrato a mano nell'anagrafica
                                        automezzi di Interacta
                                    </li>
                                    <li>
                                        <strong>Posizione serale:</strong> il cantiere è la sede in cui il mezzo si trovava
                                        secondo il GPS alla rilevazione serale delle 19:30 — la stessa logica della pagina
                                        Mezzi Disponibili Live
                                    </li>
                                </ul>
                                <p style={{ marginTop: '10px' }}>
                                    Nella tab <strong>Posizione serale</strong> compaiono due etichette aggiuntive:
                                    <strong> Altro</strong> per i mezzi che la sera non si trovavano entro il raggio di nessuna
                                    sede aziendale, e <strong>Non rilevato</strong> per quelli privi di posizione GPS. Il simbolo
                                    <strong> ≠</strong> accanto al cantiere segnala un mezzo che la sera si trovava in un
                                    cantiere diverso da quello indicato in anagrafica; sotto al cantiere rilevato viene
                                    scritto quello dell'anagrafica, e passando sopra al simbolo si vede la data della
                                    rilevazione. Il filtro <strong>Discordanze</strong> isola questi mezzi.
                                </p>
                                <p style={{ marginTop: '10px' }}>
                                    La riga <strong>"Posizioni aggiornate al …"</strong> sotto le tab indica di quale
                                    giornata è la fotografia: se resta indietro di giorni significa che la rilevazione
                                    serale non sta più girando. L'orario a fianco è invece il segnale GPS più recente
                                    presente nei dati, non l'ora dello scatto: i mezzi smettono di trasmettere quando
                                    vengono spenti, quindi di norma è l'ora del rientro di fine turno.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Filtri e visualizzazione</h4>
                                <p>
                                    È possibile filtrare i veicoli per <strong>tipologia</strong> e <strong>cantiere</strong>, cercare per <strong>targa</strong>,
                                    e scegliere tra visualizzazione a <strong>tabella</strong> o a <strong>cards</strong>. Le colonne dei bimestri possono essere
                                    mostrate o nascoste tramite i pulsanti di toggle in fondo alla pagina.
                                    I campi di <strong>Mostra Filtri</strong> si applicano con il pulsante <strong>Applica</strong>.
                                </p>
                                <p style={{ marginTop: '10px' }}>
                                    La terza tab, <strong>Tagliandi</strong>, mostra la situazione dei tagliandi a chilometri
                                    di ogni mezzo: aprila e premi di nuovo "i" per la sua spiegazione.
                                </p>
                            </div>
                            </>)}
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="content-area">
                {/* Tab: cambia solo la sorgente del cantiere (anagrafica vs posizione serale) */}
                <FiltroCategorie
                    categories={MOCKED_MANUTENZIONE_CATEGORIES}
                    filteredCategory={setSelectedTab}
                    customCss={"reportistica-section"}
                />

                {isTabPosizione && ultimaRilevazione && (
                    <div className="posizione-freshness">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span>
                            Posizioni aggiornate al <strong>{ultimaRilevazione.data}</strong>
                            <span className="posizione-freshness-dettaglio">
                                {' '}· segnale GPS più recente delle {ultimaRilevazione.ora}
                            </span>
                        </span>
                    </div>
                )}

                {selectedTab === TAB_TAGLIANDI ? (
                    <ManutenzioneTagliandi />
                ) : loading ? (
                    <Lf23Loader />
                ) : (
                    <>
                        {/* Controls Section */}
                        <div className="controls-section">
                            {/* Toggle filtri */}
                            <button
                                className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                </svg>
                                {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </button>

                            {/* Filter Panel */}
                            {showFilters && (
                                <div className="filter-panel">
                                    <div className="filter-grid">
                                        <div className="filter-group">
                                            <label>Cerca per targa</label>
                                            <div className="search-box-filter">
                                                <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="11" cy="11" r="8"></circle>
                                                    <path d="m21 21-4.35-4.35"></path>
                                                </svg>
                                                <input
                                                    type="text"
                                                    className="search-input"
                                                    placeholder="Inserisci targa..."
                                                    value={bozzaSearch}
                                                    onChange={(e) => setBozzaSearch(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === "Enter") handleApplyFilters(); }}
                                                />
                                            </div>
                                        </div>

                                        <div className="filter-group">
                                            <label>Tipologia</label>
                                            <Select
                                                isMulti
                                                placeholder="Tutte le tipologie"
                                                options={tipologieUniche.map(tip => ({value:tip, label: tip}))}
                                                value={bozzaTipologia.map(tip => ({value:tip, label:tip}))}
                                                onChange={(selected) => setBozzaTipologia(selected ? selected.map(s => s.value) : [])}
                                                classNamePrefix="filter-select"
                                                closeMenuOnSelect={false}
                                            />
                                        </div>
                                    </div>

                                    <div className="filter-actions">
                                        <button className="filter-reset-btn" onClick={handleResetFilters}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                                <path d="M3 3v5h5"></path>
                                            </svg>
                                            Reset
                                        </button>
                                        <button className="filter-apply-btn" onClick={handleApplyFilters}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            Applica
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* View toggle + Results */}
                            <div className="controls-compact">
                                <div className="controls-right">
                                    <div className="view-toggle-compact">
                                        <button
                                            className={`view-btn-compact ${viewMode === 'table' ? 'active' : ''}`}
                                            onClick={() => setViewMode('table')}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="3" width="7" height="7"></rect>
                                                <rect x="14" y="3" width="7" height="7"></rect>
                                                <rect x="14" y="14" width="7" height="7"></rect>
                                                <rect x="3" y="14" width="7" height="7"></rect>
                                            </svg>
                                            Tabella
                                        </button>
                                        <button
                                            className={`view-btn-compact ${viewMode === 'cards' ? 'active' : ''}`}
                                            onClick={() => setViewMode('cards')}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="3" width="7" height="7"></rect>
                                                <rect x="14" y="3" width="7" height="7"></rect>
                                                <rect x="14" y="14" width="7" height="7"></rect>
                                                <rect x="3" y="14" width="7" height="7"></rect>
                                            </svg>
                                            Cards
                                        </button>
                                    </div>

                                    <div className="results-badge">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                        {filteredData.length} veicol{filteredData.length === 1 ? 'o' : 'i'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* KPI Cards */}
                        <div className={`stats-grid-compact${isTabPosizione ? ' stats-grid-5' : ''}`}>
                            <div className="stat-card-compact stat-primary">
                                <div className="stat-icon-compact">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="9" cy="7" r="4"></circle>
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                    </svg>
                                </div>
                                <div className="stat-content-compact">
                                    <p className="stat-label-compact">Totale Veicoli</p>
                                    <h3 className="stat-value-compact">{stats.totaleVeicoli}</h3>
                                </div>
                            </div>

                            <div className="stat-card-compact stat-success">
                                <div className="stat-icon-compact">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                </div>
                                <div className="stat-content-compact">
                                    <p className="stat-label-compact">Manutenzione Completa</p>
                                    <h3 className="stat-value-compact">{stats.manutenzioneCompleta}</h3>
                                </div>
                            </div>

                            <div className="stat-card-compact stat-warning">
                                <div className="stat-icon-compact">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                    </svg>
                                </div>
                                <div className="stat-content-compact">
                                    <p className="stat-label-compact">Manutenzione Parziale</p>
                                    <h3 className="stat-value-compact">{stats.manutenzioneParziale}</h3>
                                </div>
                            </div>

                            <div className="stat-card-compact stat-danger">
                                <div className="stat-icon-compact">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="15" y1="9" x2="9" y2="15"></line>
                                        <line x1="9" y1="9" x2="15" y2="15"></line>
                                    </svg>
                                </div>
                                <div className="stat-content-compact">
                                    <p className="stat-label-compact">Senza Manutenzione</p>
                                    <h3 className="stat-value-compact">{stats.senzaManutenzione}</h3>
                                </div>
                            </div>

                            {isTabPosizione && (
                                <div className="stat-card-compact stat-info">
                                    <div className="stat-icon-compact">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                        </svg>
                                    </div>
                                    <div className="stat-content-compact">
                                        <p className="stat-label-compact">In Cantiere Diverso</p>
                                        <h3 className="stat-value-compact">{stats.inCantiereDiverso}</h3>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Cantieri Toggles */}
                        {cantieriUnici.length > 0 && (
                            <div className="column-toggles">
                                <span className="column-toggle-label">Cantieri:</span>
                                <button
                                    className={`column-toggle-btn ${selectedCantieri.length === cantieriUnici.length ? 'active' : ''}`}
                                    onClick={() => {
                                        if (selectedCantieri.length === cantieriUnici.length) {
                                            setSelectedCantieri([]);
                                        } else {
                                            setSelectedCantieri(cantieriUnici);
                                        }
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        {selectedCantieri.length === cantieriUnici.length ? (
                                            <>
                                                <path d="M9 11l3 3L22 4"></path>
                                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                                            </>
                                        ) : (
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        )}
                                    </svg>
                                    {selectedCantieri.length === cantieriUnici.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                                </button>
                                {cantieriUnici.map((cantiere) => (
                                    <button
                                        key={cantiere}
                                        className={`column-toggle-btn ${selectedCantieri.includes(cantiere) ? 'active' : ''}`}
                                        onClick={() => {
                                            if (selectedCantieri.includes(cantiere)) {
                                                setSelectedCantieri(selectedCantieri.filter(c => c !== cantiere));
                                            } else {
                                                setSelectedCantieri([...selectedCantieri, cantiere]);
                                            }
                                        }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            {selectedCantieri.includes(cantiere) ? (
                                                <>
                                                    <path d="M9 11l3 3L22 4"></path>
                                                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                                                </>
                                            ) : (
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            )}
                                        </svg>
                                        {cantiere}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Filtro discordanze (solo tab posizione serale) */}
                        {isTabPosizione && (
                            <div className="column-toggles">
                                <span className="column-toggle-label">Discordanze:</span>
                                <button
                                    className={`column-toggle-btn ${soloSpostati ? 'active' : ''}`}
                                    onClick={() => setSoloSpostati(!soloSpostati)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        {soloSpostati ? (
                                            <>
                                                <path d="M9 11l3 3L22 4"></path>
                                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                                            </>
                                        ) : (
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        )}
                                    </svg>
                                    Solo mezzi in cantiere diverso
                                </button>
                            </div>
                        )}

                        {/* Column Visibility Toggles */}
                        <div className="column-toggles">
                            <span className="column-toggle-label">Colonne visibili:</span>
                            {labels.map((label, index) => (
                                <button
                                    key={index}
                                    className={`column-toggle-btn ${visibleColumns[index] ? 'active' : ''}`}
                                    onClick={() => setVisibleColumns(prev => ({ ...prev, [index]: !prev[index] }))}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        {visibleColumns[index] ? (
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                        ) : (
                                            <>
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                            </>
                                        )}
                                    </svg>
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* Table or Cards View */}
                        {filteredData.length === 0 ? (
                            <div className="empty-state">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                <p>Nessun veicolo trovato</p>
                            </div>
                        ) : viewMode === 'table' ? (
                            <>
                            <div className="fullscreen-btn-row">
                                <button
                                    className="fullscreen-btn-compact"
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreen ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <div className={`table-container-compact${isFullscreen ? ' fullscreen-table' : ''}`}>
                                <RotateDevice active={isFullscreen} />
                                {isFullscreen && (
                                    <div className="fullscreen-exit-bar">
                                        <button
                                            className="fullscreen-btn-compact"
                                            onClick={() => setIsFullscreen(false)}
                                            title="Esci da tutto schermo"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                <div className="table-wrapper">
                                    <table className="data-table-compact">
                                        <thead>
                                            <tr>
                                                <th onClick={() => handleSort('targa')} style={{ cursor: 'pointer' }}>
                                                    Veicolo
                                                    {sortKey === 'targa' && (
                                                        <span> {sortDir === 'asc' ? '↑' : '↓'}</span>
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('cantiere')} style={{ cursor: 'pointer' }}>
                                                    Cantiere
                                                    {sortKey === 'cantiere' && (
                                                        <span> {sortDir === 'asc' ? '↑' : '↓'}</span>
                                                    )}
                                                </th>
                                                {labels.map((label, index) => visibleColumns[index] && (
                                                    <th key={index}>
                                                        {label}
                                                        <div className="progress-bar-header">
                                                            <div
                                                                className="progress-fill-header"
                                                                style={{ width: `${bimestreStats[index]?.percentage || 0}%` }}
                                                            />
                                                        </div>
                                                        <div className="progress-label-header">
                                                            {bimestreStats[index]?.percentage || 0}% • {bimestreStats[index]?.filled || 0}/{bimestreStats[index]?.total || 0}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((veicolo, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="vehicle-info">
                                                            <span className="vehicle-icon">{getVehicleIcon(veicolo.tipologia)}</span>
                                                            <div>
                                                                <div className="vehicle-targa">{veicolo.targa}</div>
                                                                <div className="vehicle-tipo">{veicolo.tipologia}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cantiere-cell">
                                                            <div className="cantiere-riga">
                                                                <span className="cantiere-nome">{getCantiere(veicolo) || '—'}</span>
                                                                {isTabPosizione && isSpostato(veicolo) && (
                                                                    <span
                                                                        className="cantiere-badge-spostato"
                                                                        tabIndex={0}
                                                                        aria-label={tooltipRilevazione(veicolo)}
                                                                        onMouseEnter={(e) => showTooltip(e, tooltipRilevazione(veicolo))}
                                                                        onMouseLeave={hideTooltip}
                                                                        onFocus={(e) => showTooltip(e, tooltipRilevazione(veicolo))}
                                                                        onBlur={hideTooltip}
                                                                    >
                                                                        ≠
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {isTabPosizione && isSpostato(veicolo) && (
                                                                <span className="cantiere-anagrafica">
                                                                    da anagrafica: {veicolo.cantiere}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    {veicolo.bimestri.map((bimestre, idx) => {
                                                        if (!visibleColumns[idx]) return null;
                                                        const status = getMaintenanceStatus(bimestre.value);
                                                        return (
                                                            <td key={idx}>
                                                                <div
                                                                    className="maintenance-cell"
                                                                    style={{
                                                                        backgroundColor: status.color,
                                                                        borderColor: status.borderColor,
                                                                        color: status.textColor
                                                                    }}
                                                                >
                                                                    <span className="maintenance-icon">{status.icon}</span>
                                                                    <span className="maintenance-text">{status.text}</span>
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </>
                        ) : (
                            <div className="cards-grid-compact">
                                {filteredData.map((veicolo, index) => (
                                    <div key={index} className="data-card-compact">
                                        <div className="card-header-compact">
                                            <span className="vehicle-icon-large">{getVehicleIcon(veicolo.tipologia)}</span>
                                            <div className="card-title-info">
                                                <div className="card-title-compact">{veicolo.targa}</div>
                                                <div className="card-subtitle-compact">{veicolo.tipologia}</div>
                                                <div className="card-cantiere">
                                                    <span>{getCantiere(veicolo) || '—'}</span>
                                                    {isTabPosizione && isSpostato(veicolo) && (
                                                        <span
                                                            className="cantiere-badge-spostato"
                                                            tabIndex={0}
                                                            aria-label={tooltipRilevazione(veicolo)}
                                                            onMouseEnter={(e) => showTooltip(e, tooltipRilevazione(veicolo))}
                                                            onMouseLeave={hideTooltip}
                                                            onFocus={(e) => showTooltip(e, tooltipRilevazione(veicolo))}
                                                            onBlur={hideTooltip}
                                                        >
                                                            ≠
                                                        </span>
                                                    )}
                                                </div>
                                                {isTabPosizione && isSpostato(veicolo) && (
                                                    <div className="card-cantiere-anagrafica">
                                                        da anagrafica: {veicolo.cantiere}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="card-body-compact">
                                            {veicolo.bimestri.map((bimestre, idx) => {
                                                if (!visibleColumns[idx]) return null;
                                                const status = getMaintenanceStatus(bimestre.value);
                                                return (
                                                    <div key={idx} className="card-row-maintenance">
                                                        <span className="card-label-bimestre">{labels[idx]}</span>
                                                        <div
                                                            className="maintenance-badge"
                                                            style={{
                                                                backgroundColor: status.color,
                                                                borderColor: status.borderColor,
                                                                color: status.textColor
                                                            }}
                                                        >
                                                            <span>{status.icon}</span>
                                                            <span>{status.text}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {tooltip && (
                <div
                    className={`cantiere-tooltip${tooltip.sotto ? ' sotto' : ''}`}
                    style={{ left: tooltip.x, top: tooltip.y }}
                    role="tooltip"
                >
                    {tooltip.text}
                </div>
            )}
        </div>
    );
};

export default ManutenzioneAutomezzi;
